import type { ErrorCode, JsonValue, ProblemParams } from '@volee/api-client'

import {
  AGE_GROUPS,
  seasonLabel,
  TEAM_GENDERS,
  translateDomainValue,
  underAgeLabel,
} from './domain.ts'
import { formatDate } from './format.ts'
import { EN_ERRORS, EN_NETWORK_ERROR, EN_UNKNOWN_ERROR } from './en/errors.ts'
import { IT_ERRORS, IT_NETWORK_ERROR, IT_UNKNOWN_ERROR } from './it/errors.ts'
import { locale } from './locale.ts'

// Picks the active language's error catalogue and interpolates `params` (frontend-v1 §4.5).
// `title`/`detail` are never read: English, for developers, logs only.
/** The catalogue of the active language. */
export const ERRORS: Record<ErrorCode, string> = locale === 'en' ? EN_ERRORS : IT_ERRORS

/** Shown when the backend sends a `code` this catalogue does not know. */
export const UNKNOWN_ERROR = locale === 'en' ? EN_UNKNOWN_ERROR : IT_UNKNOWN_ERROR

/** Shown when the network never answered: there is no `code` to translate. */
export const NETWORK_ERROR = locale === 'en' ? EN_NETWORK_ERROR : IT_NETWORK_ERROR

function isKnownErrorCode(code: string): code is ErrorCode {
  return Object.hasOwn(ERRORS, code)
}

/** Interpolates the problem document's `params`: `{club_id}` → value. A placeholder with no
 *  param stays put rather than becoming `undefined`, which would mutilate the sentence. */
function interpolateParams(text: string, params: ProblemParams | null): string {
  if (params === null) return text
  return text.replace(/\{(\w+)\}/g, (placeholder, key: string) => {
    const value = params[key]
    return value == null ? placeholder : String(value)
  })
}

/** Which params carry a raw backend enum value, keyed by name and not by `ErrorCode`: a
 * future code reusing one of these names is covered for free, through the same catalogue
 * a component would use — so an unknown value gives `UNKNOWN_VALUE`, never itself. */
const DOMAIN_PARAM_TRANSLATORS: Record<
  string,
  (value: JsonValue | undefined, params: ProblemParams) => string
> = {
  age_group: (value) => translateDomainValue(AGE_GROUPS, String(value)),
  gender: (value) => translateDomainValue(TEAM_GENDERS, String(value)),
  under_age: (value, params) => underAgeLabel(String(params.age_group), Number(value)),
  season_year: (value) => seasonLabel(Number(value)),
  // A `date` raw is `2026-07-01` inside an Italian sentence — the same guardrail
  // `age_group` broke, in another shape.
  starts_on: (value) => formatDate(String(value)),
}

/** Translates the params a message will interpolate, before it does: `{gender}` must never
 * become the English "male"/"female" mid-Italian sentence. Params this catalogue does not
 * recognise as domain values (`club_id`, `group_count`, …) pass through unchanged. */
function translateDomainParams(params: ProblemParams | null): ProblemParams | null {
  if (params === null) return null
  const translated: Record<string, JsonValue | undefined> = { ...params }
  for (const [key, translate] of Object.entries(DOMAIN_PARAM_TRANSLATORS)) {
    if (Object.hasOwn(params, key)) translated[key] = translate(params[key], params)
  }
  return translated
}

/** The message for an API error. An unknown `code` — the backend can add one before this
 *  frontend does — falls back to the generic text. */
export function translateError(
  code: string | null | undefined,
  params: ProblemParams | null = null,
): string {
  if (code == null || !isKnownErrorCode(code)) return UNKNOWN_ERROR
  return interpolateParams(ERRORS[code], translateDomainParams(params))
}
